import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, arrayRemove } from 'firebase/firestore';
import { useAuth } from '../../../../Context/AuthContext';
import { db } from '../../../../Firebase';

type Crypto = {
  id: string;
  name: string;
  image: string;
  current_price: number;
  price_change_percentage_24h: number;
  market_cap: number;
};

type WatchlistProps = {
  watchlist: string[];
  cryptos: Crypto[];
  onRemoveFromWatchlist: (coinId: string) => void;
};

const Watchlist: React.FC<WatchlistProps> = ({ cryptos, onRemoveFromWatchlist }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [userWatchlist, setUserWatchlist] = useState<string[]>([]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price).replace(',', ' ');
  };

  const formatMarketCap = (marketCap: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      notation: 'compact',
      compactDisplay: 'short',
    }).format(marketCap);
  };

  const handleCoinClick = (coinId: string) => {
    navigate(`/coins/${coinId}`);
  };

  const handleRemoveFromWatchlist = async (coinId: string) => {
    try {
      if (!currentUser) return;
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        watchlist: arrayRemove(coinId)
      });

      setUserWatchlist(prev => prev.filter(id => id !== coinId));
      onRemoveFromWatchlist(coinId);
    } catch (error) {
      console.error('Error removing from watchlist:', error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser) return;
      const userRef = doc(db, 'users', currentUser.uid);
      const snapshot = await getDoc(userRef);

      if (snapshot.exists()) {
        setUserWatchlist(snapshot.data()?.watchlist || []);
      }
    };

    fetchData();
  }, [currentUser]);

  return (
    <div className="w-full px-4 py-6 sm:px-6 xl:px-8">
      <h4 className="mb-4 text-lg font-semibold text-white sm:mb-6 sm:text-xl">My Watchlist</h4>

      {userWatchlist.length === 0 ? (
        <div className="text-center py-4">
          <p className="text-white">No coins in your Watchlist</p>
        </div>
      ) : (
        <div className="space-y-4">
          {userWatchlist.map((coinId, index) => {
            const crypto = cryptos.find(c => c.id === coinId);
            if (!crypto) return null;

            return (
              <div
                key={crypto.id}
                onClick={() => handleCoinClick(crypto.id)}
                className="cursor-pointer rounded-md bg-[#0b1e3c] p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between sm:gap-4 hover:bg-[#132b4f] transition"
              >
                <div className="flex items-center gap-4">
                  <span className="text-white font-semibold">{index + 1}</span>
                  <img src={crypto.image} alt={crypto.name} className="h-8 w-8" />
                  <span className="text-white font-medium">{crypto.name}</span>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-6 mt-4 sm:mt-0 text-sm text-white text-center">
                  <div>
                    <p className="opacity-70">Price</p>
                    <p>{formatPrice(crypto.current_price)}</p>
                  </div>
                  <div>
                    <p className="opacity-70">24h</p>
                    <p className={crypto.price_change_percentage_24h >= 0 ? 'text-green-400' : 'text-red-400'}>
                      {crypto.price_change_percentage_24h.toFixed(2)}%
                    </p>
                  </div>
                  <div>
                    <p className="opacity-70">Market Cap</p>
                    <p>{formatMarketCap(crypto.market_cap)}</p>
                  </div>
                  <div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveFromWatchlist(crypto.id);
                      }}
                      className="bg-red-600 hover:bg-red-700 px-3 py-1 rounded text-white"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Watchlist;
  






